// eslint-disable-next-line
import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { Tooltip, Typography } from '@mui/material';
import { getActiveAssetActiveZone, selectActiveAsset } from 'GeminiViewerComponent/features/asset/assetSlice';
import { setCompanionApp } from 'GeminiViewerComponent/features/companionApps/companionAppsSlice';
import { selectContentPanelItemlId } from 'GeminiViewerComponent/features/contentPanel/contentPanelItemSlice';
import { selectActiveTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { LrsVerbs } from 'GeminiViewerComponent/features/xAPI/xApiData';
import { saveStatement } from 'GeminiViewerComponent/features/xAPI/xapiSlice';
import { DisplayTypes, LinkTypeIds, mapPrefixUrl, mapVideoContentURL, mapPdfContentURL, prefixUrl, ContentTypes } from 'GeminiViewerComponent/helpers';
import { accountsSlice } from 'app/store';
import { config } from 'gemini-config';
import { displayProcedureContentVersion, setLinkDisplayVis, setPanelVis, setProcedureSavedView } from '../../features/globals/visibilitySlice';
import { setActiveAssetProcedureId } from '../Procedure/features/checklistDataSlice';
import { clearCurrentNode, resetCurrentTree, setActiveTreeId } from '../Procedure/features/procedureSlice';
import { linkButtonStyles } from './styles';
const { selectActiveUser } = accountsSlice;

const LinkButton = ({ link, allLinks, setOpen, idx, contentType }) => {
    const dispatch = useDispatch();
    const store = useStore();
    const theme = useSelector(selectActiveTheme);
    const classes = linkButtonStyles(theme);
    const itemId = useSelector(selectContentPanelItemlId);
    const activeZone = useSelector(getActiveAssetActiveZone);
    const activeAsset = useSelector(selectActiveAsset);
    const activeUser = useSelector(selectActiveUser);
    const s3Prefix = activeUser?.s3_prefix;

    const linkTypeId = link.link_type_id ? link.link_type_id : link.link_type.link_type_id;

    const handleLink = useCallback(async () => {
        if (
            config.modeType === 'author' &&
            (link?.display_type_id === DisplayTypes.CompanionApp.id ||
                link?.display_type?.display_type_id === DisplayTypes.CompanionApp.id)
        ) {
            setOpen(true);
        }
        dispatch(
            saveStatement({
                LrsVerb: LrsVerbs.Accessed,
                response: {
                    linkType: link.display_type_id ? link.display_type_id : link.display_type.display_type_id,
                    url: link.content_version?.url,
                    linkContent: link.content_version?.url
                        ? link.content_version.url
                        : link.content_version?.embed_data,
                },
                linkId: link?.link_id,
                itemId,
                zoneId: activeZone?.zone_id,
            })
        );
        const companionData = {
            display_name: link.display_name,
            app_url:
                link?.content_version?.encoding_status &&
                    link?.content_version?.encoding_status.toLowerCase() === 'complete'
                    ? link?.content_version?.encoded_url
                    : link.content_version?.url
                        ? link.content_version.url
                        : link.content_version?.embed_data,
            app_mode: 1,
            linkType: linkTypeId,
            linkData: link.content,
        };

        const displayTypeId = link.display_type_id ? link.display_type_id : link.display_type.display_type_id;

        if (
            (displayTypeId === DisplayTypes.CompanionApp.id || displayTypeId === DisplayTypes.Inline.id) &&
            linkTypeId === LinkTypeIds.Procedure
        ) {
            try {
                const viewParams = window?.geminiViewManager?.getCurrentViewParams?.();
                const state = store.getState();
                dispatch(
                    setProcedureSavedView({
                        assetId: activeAsset?.asset_id ?? null,
                        zoneId: activeZone?.zone_id ?? null,
                        viewParams: viewParams ?? null,
                        assetStoreState: state.asset,
                        procedureStoreState: state.procedure,
                    })
                );
            } catch (e) {
                const state = store.getState();
                dispatch(
                    setProcedureSavedView({
                        assetId: activeAsset?.asset_id ?? null,
                        zoneId: activeZone?.zone_id ?? null,
                        viewParams: null,
                        assetStoreState: state.asset,
                        procedureStoreState: state.procedure,
                    })
                );
            }
        }
        switch (displayTypeId) {
            case DisplayTypes.CompanionApp.id:
                if (linkTypeId === LinkTypeIds.Procedure) {
                    dispatch(setActiveAssetProcedureId(link?.content_version?.procedure_id));
                    dispatch(setPanelVis({ open: false }));
                    dispatch(displayProcedureContentVersion(link.content_version));
                    dispatch(setCompanionApp({}));
                } else if (linkTypeId === LinkTypeIds.SME) {
                    dispatch(
                        setCompanionApp({
                            ...companionData,
                            linkData: link.content,
                        })
                    );
                } else if (linkTypeId === LinkTypeIds.Video) {
                    dispatch(
                        setCompanionApp({
                            ...companionData,
                            skill_id: link.content.skill_id,
                        })
                    );
                } else {
                    dispatch(
                        setCompanionApp({
                            ...companionData,
                        })
                    );
                }
                break;
            case DisplayTypes.External.id:
                window.open(link.content_version.url || link.content_version.encoded_url, '_blank');
                break;
            case DisplayTypes.Inline.id:
                if (linkTypeId === LinkTypeIds.Procedure) {
                    // Have to clear current node and await a render to force a state change - otherwise
                    // the useEffect in procedure/index.jsx does not always trigger depending on the current
                    await dispatch(clearCurrentNode());
                    dispatch(setActiveAssetProcedureId(link.content_version?.procedure?.procedure_id));
                    dispatch(setActiveTreeId(link.content_version?.procedure?.procedure_id));
                    dispatch(resetCurrentTree());
                    dispatch(setPanelVis({ open: false }));
                    dispatch(displayProcedureContentVersion(link.content_version));
                    dispatch(setCompanionApp({}));
                } else {
                    // Links can be duplicated in the side panel, but are not duplicated in the
                    // link display modal. Grab the first link if it is duplicated since only the first
                    // link will be part of the link display modal.

                    const firstLink = allLinks.find(
                        (l) =>
                            (typeof l.content_version === 'object' &&
                                typeof link.content_version === 'object' &&
                                l.content_version.content_version_id === link.content_version.content_version_id) ||
                            (typeof l.content === 'object' &&
                                typeof link.content === 'object' &&
                                l.content.content_id == link.content.content_id) ||
                            (link.content_id && l.content_id === link.content_id)
                    );

                    let linkContent = null;

                    if (linkTypeId === LinkTypeIds.Document && link.content?.content_type_id === ContentTypes.Pdf.id) {
                        linkContent = mapPdfContentURL(firstLink);
                    } else {
                        linkContent = mapVideoContentURL(firstLink);
                    }


                    if (linkTypeId === LinkTypeIds.Learning && link.content_version.launch_file) {
                        dispatch(
                            setLinkDisplayVis({
                                open: true,
                                chapter_data: link?.chapter,
                                allLinks: mapPrefixUrl(allLinks, s3Prefix, 'encoded_url'),
                                linkContent: link.content_version?.url,
                                linkData: {
                                    ...firstLink,
                                    ...firstLink?.content,
                                    ...(typeof firstLink.content_version === 'object' ? firstLink.content_version : {}),
                                },
                                linkTypeId: linkTypeId,
                                autoPlay: Boolean(firstLink?.auto_play),
                                overlayText: firstLink.content_version?.overlay_text,
                            })
                        );
                    } else {
                        let finalLinkContent = null;
                        if (linkTypeId === LinkTypeIds.Document && link.content?.content_type_id === ContentTypes.Pdf.id) {
                            finalLinkContent = linkContent;
                        } else if (linkTypeId === LinkTypeIds.Embed) {
                            finalLinkContent = linkContent;
                        } else {
                            finalLinkContent = prefixUrl(linkContent, s3Prefix);
                        }

                        dispatch(
                            setLinkDisplayVis({
                                open: true,
                                chapter_data: link?.chapter,
                                //  ||
                                // link?.content_version?.chapters?.[0],
                                allLinks: mapPrefixUrl(allLinks, s3Prefix, 'encoded_url'),
                                linkContent: finalLinkContent,
                                linkData: {
                                    ...firstLink,
                                    ...firstLink?.content,
                                    ...(typeof firstLink.content_version === 'object' ? firstLink.content_version : {}),
                                },
                                linkTypeId: linkTypeId,
                                autoPlay: Boolean(firstLink?.auto_play),
                                overlayText: firstLink.content_version?.overlay_text,
                            })
                        );
                    }
                }
                break;
            default:
                break;
        }
    }, [allLinks]);

    return (
        <div
            id={`viewer_zone-media_${contentType}_item-${idx}`}
            data-content-type={contentType}
            data-link-id={link.link_id}
            className={classes.linkContainer}
            onClick={handleLink}
        >
            <Tooltip title={link.display_name ?? ''}>
                <Typography
                    overflow="hidden"
                    textOverflow="ellipsis"
                    display="inline-block"
                    whiteSpace="nowrap"
                    width="100%"
                >
                    {link.display_name}
                </Typography>
            </Tooltip>
            {link?.description && (
                <Typography
                    className={classes.description}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    display="inline-block"
                    whiteSpace="nowrap"
                    width="100%"
                >
                    {link.description}
                </Typography>
            )}
        </div>
    );
};

export { LinkButton };
